import React, { useEffect, useState } from "react";
import { MDBTooltip } from "mdb-react-ui-kit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faFileInvoice, faPrint, faTag } from "@fortawesome/free-solid-svg-icons";
import RightDrawer from "../RightDrawer";
import { formatLocalizedDateTimeParts } from "../../utils/formatDateTime";
import headerIcon from "./shipment-drawer-icons/header.svg";
import copyIcon from "./shipment-drawer-icons/copy.svg";
import closeIcon from "./shipment-drawer-icons/close.svg";
import sarLg from "./shipment-drawer-icons/sar-lg.svg";
import sarSm from "./shipment-drawer-icons/sar-sm.svg";
import datesIcon from "./shipment-drawer-icons/dates.svg";
import requestedIcon from "./shipment-drawer-icons/requested.svg";
import merchantIcon from "./shipment-drawer-icons/merchant.svg";
import shipmentIcon from "./shipment-drawer-icons/shipment.svg";
import pinIcon from "./shipment-drawer-icons/pin.svg";
import carrierIcon from "./shipment-drawer-icons/carrier.svg";
import timelineIcon from "./shipment-drawer-icons/timeline.svg";
import { shipmentLabelHref } from "../../utils/shipmentLabelUrl";
import "./shipment-details-drawer.css";

function haversineKm(pickup, dropoff) {
  const lat1 = Number(pickup?.lat);
  const lng1 = Number(pickup?.lng);
  const lat2 = Number(dropoff?.lat);
  const lng2 = Number(dropoff?.lng);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return null;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 10) / 10;
}

function formatWhen(value, dateLocale) {
  if (!value) return "";
  const parts = formatLocalizedDateTimeParts(value, dateLocale, { withSeconds: false });
  if (!parts?.date) return "";
  return `${parts.date}, ${parts.time}`;
}

function trackingPageUrl(rawUrl, language) {
  const raw = String(rawUrl || "").trim();
  if (!raw) return "";
  const lang = String(language || "en").toLowerCase().startsWith("ar") ? "ar" : "en";
  try {
    const url = new URL(raw);
    if (!url.searchParams.has("lang")) {
      url.searchParams.set("lang", lang);
    }
    return url.toString();
  } catch {
    return raw;
  }
}

function Field({ label, value, muted = false, ltr = false, divider = false, dividerTop = false }) {
  const empty = value == null || value === "";
  const valueClass = [
    "sdd__value",
    muted ? "sdd__value--muted" : "",
    ltr ? "sdd__value--ltr" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const fieldClass = [
    "sdd__field",
    divider ? "sdd__field--divider" : "",
    dividerTop ? "sdd__field--divider-top" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={fieldClass}>
      <span className="sdd__label">{label}</span>
      <span className={valueClass} dir={ltr ? "ltr" : "auto"}>
        {empty ? "\u00a0" : value}
      </span>
    </div>
  );
}

function Money({ value, large = false }) {
  const amount = Number(value);
  const text = Number.isFinite(amount) ? amount.toFixed(2) : "";
  return (
    <span className={`sdd__money${large ? " sdd__money--lg" : ""}`} dir="ltr">
      <img src={large ? sarLg : sarSm} alt="" />
      <span>{text || "\u00a0"}</span>
    </span>
  );
}

function Section({ icon, title, children, cardClassName = "sdd__card" }) {
  return (
    <section className="sdd__section">
      <h3 className="sdd__section-title">
        {icon ? <img src={icon} alt="" /> : null}
        {title}
      </h3>
      <div className={cardClassName}>{children}</div>
    </section>
  );
}

function DocIconLink({ href, label, icon }) {
  return (
    <MDBTooltip tag="span" title={label} placement="top" wrapperProps={{ className: "sdd__icon-link-wrap" }}>
      <span className="sdd__icon-link-wrap">
        <a
          className="sdd__icon-link"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
        >
          <FontAwesomeIcon icon={icon} />
        </a>
      </span>
    </MDBTooltip>
  );
}

function ProofGallery({ title, urls }) {
  if (!urls?.length) return null;
  return (
    <div className="sdd__proof">
      <span className="sdd__label">{title}</span>
      <div className="sdd__proof-grid">
        {urls.map((src, index) => (
          <a
            key={`${src}-${index}`}
            className="sdd__proof-link"
            href={src}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" />
          </a>
        ))}
      </div>
    </div>
  );
}

function paymentLabel(row, t) {
  const share = Number(row?.customerShare);
  if (
    row?.paymentStatus === "skipped"
    || (row?.coveredByBusiness === true && Number.isFinite(share) && share > 0)
  ) {
    return t("shipmentsPage.paymentSkipped");
  }
  if (!row?.paymentStatus && !row?.coveredByBusiness) return "";
  if (row?.coveredByBusiness && row?.paymentStatus === "paid") {
    return t("shipmentsPage.coveredByBusiness");
  }
  if (row?.paymentStatus === "paid") return t("shipmentsPage.paid");
  if (row?.paymentStatus === "expired") return t("shipmentsPage.paymentExpired");
  if (row?.paymentStatus === "canceled") return t("shipmentsPage.paymentCanceled");
  if (row?.paymentStatus === "pending_payment" || row?.status === "Pending Payment") {
    return t("shipmentsPage.statusPendingPayment");
  }
  return "";
}

function statusText(row, t) {
  const normalized = String(row?.status || "").trim().toLowerCase();
  const key = {
    delivered: "statusDelivered",
    "in transit": "statusInTransit",
    in_transit: "statusInTransit",
    assigned: "statusAssigned",
    "pending payment": "statusPendingPayment",
    scheduled: "statusScheduled",
    pending: "statusPending",
    new: "statusPending",
    cancelled: "statusCancelled",
    canceled: "statusCancelled",
    "needs review": "statusNeedsReview",
  }[normalized];
  return key ? t(`shipmentsPage.${key}`) : row?.status || "";
}

export default function ShipmentDetailsDrawer({
  open,
  row,
  onClose,
  t,
  dateLocale,
  dir = "ltr",
  language = "en",
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [row?._id, row?.shipmentId]);

  const displayId = row?.shipmentId || row?.orderRef || "";
  const business = row?.restaurantShare != null ? Number(row.restaurantShare) : null;
  const customer = row?.customerShare != null ? Number(row.customerShare) : null;
  const total =
    business != null && customer != null
      ? Math.round((business + customer) * 100) / 100
      : Number(row?.price) || 0;
  const businessPct = total > 0 && business != null ? Math.round((business / total) * 100) : 0;
  const customerPct = total > 0 && customer != null ? Math.max(0, 100 - businessPct) : 0;
  const pkg = row?.package || {};
  const dims =
    pkg.lengthCm != null && pkg.widthCm != null && pkg.heightCm != null
      ? `${pkg.lengthCm} × ${pkg.widthCm} × ${pkg.heightCm} cm`
      : "";
  const km = haversineKm(row?.pickup, row?.dropoff);
  const trackingHref = trackingPageUrl(row?.trackingUrl, language);
  const labelHref = shipmentLabelHref(row?.labelUrl);
  const labelPrintHref = shipmentLabelHref(row?.labelUrl, { print: true });
  const pickupProof = Array.isArray(row?.pickupProofImages) ? row.pickupProofImages : [];
  const dropoffProof = Array.isArray(row?.dropoffProofImages) ? row.dropoffProofImages : [];
  const showLabelInvoice =
    labelHref || (row?.paymentStatus === "paid" && row?.invoiceUrl);
  const showProof = pickupProof.length > 0 || dropoffProof.length > 0;
  const payHref =
    row?.paymentStatus === "pending_payment" && row?.paymentLinkUrl
      ? row.paymentLinkUrl
      : row?.invoiceUrl || "";
  const payLabel = row?.invoiceUrl && row?.paymentStatus === "paid"
    ? t("shipmentsPage.invoiceLink")
    : t("shipmentsPage.paymentLink");

  const copyId = async () => {
    if (!displayId) return;
    try {
      await navigator.clipboard.writeText(displayId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy shipment id", err);
    }
  };

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      hideHeader
      width="640px"
      dir={dir}
      panelClassName="sdd-panel"
    >
      {row ? (
        <div className="sdd" dir={dir}>
          <header className="sdd__header">
            <div>
              <div className="sdd__id-row">
                <img src={headerIcon} alt="" />
                <span className="sdd__id" dir="ltr">{displayId}</span>
                <button
                  type="button"
                  className="sdd__icon-btn"
                  onClick={copyId}
                  aria-label={copied ? t("shipmentsPage.shipmentIdCopied") : t("shipmentsPage.copyShipmentId")}
                >
                  {copied ? <FontAwesomeIcon icon={faCheck} /> : <img src={copyIcon} alt="" />}
                </button>
              </div>
              <span className="sdd__badge">{statusText(row, t)}</span>
            </div>
            <button
              type="button"
              className="sdd__icon-btn sdd__close"
              onClick={onClose}
              aria-label={t("shipmentsPage.details.close")}
            >
              <img src={closeIcon} alt="" />
            </button>
          </header>

          <div className="sdd__body">
            <div className="sdd__pay">
              <div className="sdd__pay-head">
                <strong>
                  <img className="sdd__pay-currency" src={sarSm} alt="" />
                  {t("shipmentsPage.payment")}
                </strong>
                {paymentLabel(row, t) ? (
                  <span className="sdd__badge">{paymentLabel(row, t)}</span>
                ) : null}
              </div>
              <span className="sdd__label">{t("shipmentsPage.details.totalDelivery")}</span>
              <div className="sdd__total">
                <Money value={total} large />
              </div>
              <div className="sdd__bar" aria-hidden="true">
                <span style={{ width: `${businessPct}%` }} />
                <span className="sdd__bar-customer" style={{ width: `${customerPct}%` }} />
              </div>
              <div className="sdd__split">
                <div>
                  <span className="sdd__label">
                    <i className="sdd__dot" />
                    {t("shipmentsPage.details.businessPays")}
                  </span>
                  <Money value={business ?? 0} />
                  <span className="sdd__hint">
                    {t("shipmentsPage.details.percentOfTotal", { percent: businessPct })}
                  </span>
                </div>
                <div>
                  <span className="sdd__label">
                    <i className="sdd__dot sdd__dot--customer" />
                    {t("shipmentsPage.details.customerPays")}
                  </span>
                  <Money value={customer ?? 0} />
                  <span className="sdd__hint">
                    {t("shipmentsPage.details.percentOfTotal", { percent: customerPct })}
                  </span>
                </div>
              </div>
              {payHref && row?.paymentStatus !== "paid" ? (
                <a className="sdd__pay-btn" href={payHref} target="_blank" rel="noopener noreferrer">
                  {payLabel}
                </a>
              ) : null}
            </div>

            {showLabelInvoice ? (
              <Section icon={shipmentIcon} title={t("shipmentsPage.details.labelAndInvoice")}>
                <div className="sdd__icon-row">
                  {labelHref ? (
                    <>
                      <DocIconLink
                        href={labelHref}
                        label={t("shipmentsPage.details.viewLabel")}
                        icon={faTag}
                      />
                      <DocIconLink
                        href={labelPrintHref}
                        label={t("shipmentsPage.details.printLabel")}
                        icon={faPrint}
                      />
                    </>
                  ) : null}
                  {row?.paymentStatus === "paid" && row?.invoiceUrl ? (
                    <DocIconLink
                      href={row.invoiceUrl}
                      label={t("shipmentsPage.invoiceLink")}
                      icon={faFileInvoice}
                    />
                  ) : null}
                </div>
              </Section>
            ) : null}

            {showProof ? (
              <Section icon={pinIcon} title={t("shipmentsPage.details.proof")}>
                <ProofGallery title={t("shipmentsPage.details.pickupProof")} urls={pickupProof} />
                <ProofGallery title={t("shipmentsPage.details.dropoffProof")} urls={dropoffProof} />
              </Section>
            ) : null}

            <Section icon={datesIcon} title={t("shipmentsPage.details.dates")}>
              <div className="sdd__grid">
                <Field
                  label={t("shipmentsPage.details.createdInOneHub")}
                  value={formatWhen(row.createdAt, dateLocale)}
                  ltr
                  divider
                />
                <Field
                  label={t("shipmentsPage.details.scheduledFor")}
                  value={formatWhen(row.scheduledAt, dateLocale)}
                  ltr
                  divider
                />
                <Field
                  label={t("shipmentsPage.details.paidAt")}
                  value={formatWhen(row.paidAt, dateLocale) || t("shipmentsPage.details.notPaidYet")}
                  muted={!row.paidAt}
                  ltr={Boolean(row.paidAt)}
                />
              </div>
            </Section>

            <Section icon={requestedIcon} title={t("shipmentsPage.details.requestedBy")}>
              <div className="sdd__grid">
                <Field label={t("shipmentsPage.details.oneHubUser")} value={row.createdBy?.name} />
                <Field label={t("shipmentsPage.details.email")} value={row.createdBy?.email} ltr />
              </div>
            </Section>

            <Section icon={merchantIcon} title={t("shipmentsPage.details.merchant")}>
              <div className="sdd__grid">
                <Field label={t("shipmentsPage.details.merchantName")} value={row.brand} divider />
                <Field
                  label={t("shipmentsPage.details.phone")}
                  value={row.pickup?.contactPhone}
                  ltr
                  divider
                />
                <Field label={t("shipmentsPage.details.storeId")} value={row.storeId} ltr />
                <Field
                  label={t("shipmentsPage.details.organizationId")}
                  value={row.businessAccountId}
                  ltr
                />
              </div>
            </Section>

            <Section icon={shipmentIcon} title={t("shipmentsPage.details.shipment")}>
              <div className="sdd__grid">
                <Field
                  label={t("shipmentsPage.details.distance")}
                  value={km != null ? `${km} km` : ""}
                  ltr
                  divider
                />
                <Field
                  label={t("shipmentsPage.details.weight")}
                  value={pkg.weightKg != null ? `${pkg.weightKg} kg` : ""}
                  ltr
                  divider
                />
                <Field label={t("shipmentsPage.details.dimensions")} value={dims} ltr />
              </div>
            </Section>

            <Section icon={pinIcon} title={t("shipmentsPage.details.pickup")}>
              <Field label={t("shipmentsPage.branch")} value={row.branch} divider />
              <div className="sdd__grid">
                <Field label={t("shipmentsPage.details.name")} value={row.pickup?.contactName} divider />
                <Field label={t("shipmentsPage.details.phone")} value={row.pickup?.contactPhone} ltr divider />
                <Field label={t("shipmentsPage.details.latitude")} value={row.pickup?.lat} ltr divider />
                <Field label={t("shipmentsPage.details.longitude")} value={row.pickup?.lng} ltr divider />
              </div>
              <Field
                label={t("shipmentsPage.details.shipmentValue")}
                value={<Money value={row.orderTotal ?? 0} />}
              />
              {row.pickup?.address ? (
                <Field label={t("shipmentsPage.details.address")} value={row.pickup.address} dividerTop />
              ) : null}
            </Section>

            <Section icon={pinIcon} title={t("shipmentsPage.details.dropoff")}>
              <Field
                label={t("shipmentsPage.details.nationalAddressShortCode")}
                value={row.dropoff?.nationalShortCode}
                ltr
                divider
              />
              <Field label={t("shipmentsPage.details.fullAddress")} value={row.dropoff?.address} divider />
              <div className="sdd__grid">
                <Field label={t("shipmentsPage.details.latitude")} value={row.dropoff?.lat} ltr divider />
                <Field label={t("shipmentsPage.details.longitude")} value={row.dropoff?.lng} ltr divider />
                <Field label={t("shipmentsPage.details.recipientName")} value={row.dropoff?.contactName} />
                <Field
                  label={t("shipmentsPage.details.recipientPhone")}
                  value={row.dropoff?.contactPhone}
                  ltr
                />
              </div>
            </Section>

            <Section icon={requestedIcon} title={t("shipmentsPage.details.driver")}>
              <div className="sdd__grid">
                <Field
                  label={t("shipmentsPage.details.driverName")}
                  value={row.courierName || t("shipmentsPage.details.driverNotAssigned")}
                  muted={!row.courierName}
                />
                <Field
                  label={t("shipmentsPage.details.driverPhone")}
                  value={row.courierPhone || t("shipmentsPage.details.driverNotAssigned")}
                  muted={!row.courierPhone}
                  ltr={Boolean(row.courierPhone)}
                />
              </div>
            </Section>

            <section className="sdd__section sdd__section--carrier">
              <h3 className="sdd__section-title">
                <img src={carrierIcon} alt="" />
                {t("shipmentsPage.details.carrier")}
              </h3>
              <div className="sdd__card">
                <div className="sdd__card-grid">
                  <div className="sdd__card-grid-row sdd__card-grid-row--divider">
                    <Field label={t("shipmentsPage.courier")} value={row.carrier} />
                    <Field label={t("shipmentsPage.details.carrierId")} value={row.deliveryId} ltr />
                  </div>
                  <div className="sdd__card-grid-row">
                    <Field label={t("shipmentsPage.details.oneHubOrderId")} value={row.orderRef} ltr />
                    <Field
                      label={t("shipmentsPage.details.trackingNo")}
                      value={row.shipmentId || row.deliveryId}
                      ltr
                    />
                  </div>
                </div>
              </div>
              {trackingHref ? (
                <a
                  className="sdd__timeline-btn"
                  href={trackingHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={timelineIcon} alt="" />
                  {t("shipmentsPage.details.viewTimeline")}
                </a>
              ) : (
                <button type="button" className="sdd__timeline-btn" disabled>
                  <img src={timelineIcon} alt="" />
                  {t("shipmentsPage.details.viewTimeline")}
                </button>
              )}
              {!trackingHref ? (
                <p className="sdd__hint sdd__hint--timeline">{t("shipmentsPage.details.noTrackingPage")}</p>
              ) : null}
            </section>
          </div>
          <footer className="sdd__footer">
            <button type="button" className="sdd__close-btn" onClick={onClose}>
              {t("shipmentsPage.details.close")}
            </button>
          </footer>
        </div>
      ) : null}
    </RightDrawer>
  );
}
